import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../constants/api';
import { notification } from 'antd';

const DataEntryTable = ({
  data,
  reportHeader,
  reportFooter,
  link,
  token,
  executionTime,
  setData,
}) => {
  const [columns, setColumns] = useState([]);
  // Step 1: Define the state to track which rows have been processed
  const [processedRows, setProcessedRows] = useState([]);

  // Initial state with the original data passed as a prop
  const [originalData, setOriginalData] = useState(data);
  const [filteredData, setFilteredData] = useState(data); // Filtered data to show based on search query

  // Create a ref for the input element
  const inputRef = useRef(null);

  useEffect(() => {
    // Fetch column definitions and data from the API
    const columns = Object.keys(data[0]);

    setColumns(
      columns.map((col) => {
        const parts = col.split('~');
        return {
          columnName: col,
          header: parts[0],
          key: parts[1] || parts[0],
          visible: parts[2] !== 'hide',
          editable: parts[2] === 'editable', // Editable only if `column_name` is present
        };
      })
    );
  }, []);
  const [search, setSearch] = useState('');

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(e.target.value);

    // Filter data based on the search query
    const filtered = originalData.filter((row) =>
      Object.values(row).some((val) =>
        val?.toString().toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredData(filtered);
  };

  const handleEdit = (e, rowIndex2, colData, row, colIndex) => {
    const columnName = colData.columnName;
    console.log(columnName, rowIndex2, colIndex, row);
    const updatedData = [...data];
    var text = e.target.textContent;

    // Find the index of the row in the original data using the row's content
    const rowIndex = updatedData.findIndex(
      (r) => JSON.stringify(r) === JSON.stringify(row)
    );

    if (rowIndex === -1) {
      return;
    }

    updatedData[rowIndex][columnName] = text;
    setData(updatedData);
    const expression = columnName?.split('~')[3]?.split(',');
    if (!expression) {
      return;
    }

    if (expression[0]) {
      calculateExpression(expression[0], rowIndex, row, updatedData, setData);
    }

    if (expression[1]) {
      calculateExpression(expression[1], rowIndex, row, updatedData, setData);
    }

    if (expression[2]) {
      calculateExpression(expression[2], rowIndex, row, updatedData, setData);
    }

    if (expression[0].includes('autotick')) handleSubmit(row, rowIndex);
  };

  function calculateExpression(
    expression,
    rowIndex,
    row,
    updatedData,
    setData
  ) {
    // Parse the string like "7-6=8"
    const parts = expression?.split('=');
    const leftSide = parts[0]; // "7-6"
    const rightSide = parts[1]; // "8" - column where result should be set

    // Create a regex that matches any of the delimiters: -, +, *, /
    const delimiterRegex = /[-+*/]/;

    // Check if the string contains any of the specified delimiters
    const match = leftSide.match(delimiterRegex);

    if (!match) {
      return;
    }

    // The first matched delimiter will be in the 'match' array
    const delimiter = match[0];
    console.log('Found delimiter:', delimiter);

    // Split the string by the found delimiter and convert to integers
    const [col1, col2] = leftSide
      .split(delimiter) // Split by the found delimiter
      .map((index) => parseInt(index.trim())); // Convert to integers

    // console.log('col1:', col1);
    // console.log('col2:', col2);

    const col3 = rightSide;
    // Convert the object keys into an array
    const keys = Object.keys(row);

    // Get the values from the row data
    let value1 = row[keys[col1]];
    let value2 = row[keys[col2]];

    // console.log('keys:', keys);

    console.log(`col1 - ${keys[col1]} Value at index ${col1} : ${value1}`);
    console.log(`col2 - ${keys[col2]} Value at index ${col2} : ${value2}`);

    // Perform the subtraction
    var result = 0;

    if (isNaN(parseFloat(value1))) {
      notification.error({
        message: `${keys[col1].split('~')[0]} Value: '${value1}'  at index ${col1} is not a number`,
      });
    }
    if (isNaN(parseFloat(value2))) {
      notification.error({
        message: `${keys[col2].split('~')[0]} Value : '${value2}' at index ${col2}  is not a number`,
      });
    }

    if (delimiter === '-') {
      result = value1 - value2;
      if (result < 0) {
        notification.error({ message: 'The difference is negative.' });
        return;
      }
    } else if (delimiter === '+') {
      result = parseFloat(value1) + parseFloat(value2);
    } else if (delimiter === '*') {
      result = parseFloat(value1) * parseFloat(value2);
    } else if (delimiter === '/') {
      result = parseFloat(value1) / parseFloat(value2);
    }

    updatedData[rowIndex][keys[col3]] =
      value1 === '' || value2 === '' ? '' : result.toFixed(2);

    setData(updatedData);
  }

  // console.log('rowData:', rowData);
  const handleSubmit = async (row, rowIndex) => {
    const filteredRow = Object.keys(row).reduce((acc, key) => {
      const parts = key.split('~');

      // Only include keys that have a valid second part (the column name)
      if (parts.length >= 2) {
        acc[parts[1]] = row[key]; // Use the column name part as the key
      }
      return acc;
    }, {});

    const route = filteredRow.api_route;
    delete filteredRow.api_route;
    console.log('Filtered row for submission:', filteredRow, 'route:', route);
    var insertUrl = `${API_URL}/${ENDPOINTS.SYS}/create/${route}`;

    const response = await axios.post(insertUrl, filteredRow, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });

    const { success, message } = response.data;
    if (success === 1) {
      setProcessedRows((prev) => [...prev, rowIndex]); // Track the processed row by ID
      // setProcessedRowsResponse((prev) => [...prev, rowIndex]); // Track the processed row by ID

      // Remove the row from the original data
      const updatedData = originalData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );

      // Update the original data
      setOriginalData(updatedData);

      // If the removed row is in the filteredData, we should also update filteredData
      const updatedFilteredData = filteredData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );

      // Update the filtered data as well (it can stay in sync with the original data)
      setFilteredData(updatedFilteredData);

      // Focus the input field when this event is triggered
      if (inputRef.current) {
        inputRef.current.focus();
      }
      setSearch('');
      notification.success({ message: message });
    } else {
      notification.error({ message: message });
    }
  };

  console.log(
    'data length: ',
    data?.length,
    ' filtered length: ',
    filteredData.length
  );
  return (
    <div className=" ">
      <div className="row">
        <div className="col-9"></div>
        <div className="col-3">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            className="form-control mb-3"
            style={{ maxWidth: '300px' }} // Optional: Limit max width for better alignment
            ref={inputRef} // Attach the ref here
          />
        </div>
      </div>
      <table className="table table-bordered">
        <thead className="table-dark">
          <tr>
            {columns
              .filter((col) => col.visible)
              .map((col) => (
                <th key={col.key}>{col.header}</th>
              ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData &&
            filteredData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns
                  .filter((col) => col.visible)
                  .map((col, colIndex) => (
                    <td
                      key={col.key}
                      contentEditable={col.editable}
                      suppressContentEditableWarning={col.editable}
                      onBlur={
                        col.editable
                          ? (e) => handleEdit(e, rowIndex, col, row, colIndex)
                          : undefined
                      }
                      style={col.editable ? { backgroundColor: '#f9f9f9' } : {}}
                    >
                      {row[col.columnName]}
                    </td>
                  ))}
                <td>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleSubmit(row, rowIndex)}
                    // disabled={processedRows.includes(rowIndex)} // Step 3: Disable if processed
                  >
                    ✅
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {executionTime !== null && (
        <p>
          Request executed in: {executionTime.toFixed(2)} sec - Total rows:
          {data && data.length} rows
        </p>
      )}
    </div>
  );
};

export default DataEntryTable;
